import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";

import { createEditorSession } from 'actions/editor';

import _ from 'lodash';
import cx from 'classnames';
import { Frame } from 'scenejs';
import { useEditorSession } from 'components/Editor/hooks/useEditorSession'

import { CONTENT_CARD, LIVE_ELEMENT, LIVE_ELEMENT_CONTAINER, MENU, SECTION } from "constants/widgetTypes";

import { EditorContext } from '../../index';
import { Property } from '../../components/properties/';
import { BrandThemeManager, FormContext, Widgets, WidgetsContext } from '@paperflite-marketing/cleverstory-commons';
import { BreakLiveElementDialog, EditLiveElementDialog } from 'components/Editor/components/liveElements';

import { HOTSPOT } from 'constants/widgetTypes';

import userRoleService from 'services/userRoles.service';
import { BrandComponentProperty } from 'components/Editor/components/brandElements';
import styles from './liveElements.module.scss';

const IMAGE_CDN_ENDPOINT = process.env.REACT_APP_IMAGE_CDN;

const LiveElementsWidgetComponent = function ({ widgetProps, widgetRef, className, style, children }) {
  const dispatch = useDispatch();

  const ref = widgetRef;

  const editorContext = React.useContext(EditorContext);
  const editor = editorContext.editorSession.id;

  const storyboard = useSelector(state => _.get(state, "storyboards.storyboard.item", null));
  const storyboardType = _.get(storyboard, 'type', '');

  const id = _.get(widgetProps, 'uid');
  const liveElementEditorSession = useEditorSession(`editors_live_element_${id}`);
  const margin = _.get(widgetProps, 'properties.desktop.margin', 0);
  const type = _.get(widgetProps, 'type');
  const breakpoint = useSelector(state => _.get(state, `${editor}.present.breakpoint`));
  const widgets = useSelector(state => {
    const widgets = _.get(state, `${liveElementEditorSession.id}.present.widgets`) || [];
    return widgets.filter(item => item.type === LIVE_ELEMENT_CONTAINER);
  });
  const livePresent = useSelector(state => _.get(state, `${liveElementEditorSession.id}.present.widgets`) || []);

  let matches = false;
  let isStretched = false;
  
  livePresent.forEach(item => {
    const isMatchingType = item.type === MENU || item.type === SECTION;
    const stretched = item.properties[breakpoint]?.stretched === true;
  
    if (isMatchingType) {
      matches = true;
      if (stretched) {
        isStretched = true;
      }
    }
  });
  const [widgetContextState, setWidgetContextState] = useState({
    selector: ``,
    storyboard
  });

  let widgetStyle = {
    ...style,
    ...(matches && isStretched && {
      marginLeft: 0,
      width: `calc(var(--editor-width) - 2 * ${margin}px) !important`,
      left: `calc((100% - var(--editor-width)) / 2) !important`,
    }),
  }
  
  // TODO: Need to fix the setTimeout issue
  useEffect(() => {
    let timer = setTimeout(() => {
      if (!matches) return;

      const translateY = _.get(widgetProps, `properties.${breakpoint}.layout.transform.translate`, '')?.split(",")?.[1] || '0px';
      if (breakpoint === 'desktop') {
        // Common desktop styles
        ref.current.style.marginLeft = '0px';

        if (storyboardType === 'SPATIAL' || storyboardType === 'INTERACTIVE_CONTENT' || !isStretched) {
          // Special case for spatial/interactive content
          ref.current.style.width = '1200px';
          ref.current.style.left = 'calc((100% - 1200px) / 2)';
          ref.current.style.transform = `translate(0px, ${translateY})`;
        } else {
          // Regular desktop layout
          ref.current.style.left = 'calc((100% - var(--editor-width)) / 2)';
          ref.current.style.width = `calc(var(--editor-width) - 2 * ${margin}px)`;
        }
      
      } else if (breakpoint === 'mobile') {
        // Mobile layout
        ref.current.style.width = '100%';
        ref.current.style.left = '0px';
        ref.current.style.marginLeft = '0px';
        ref.current.style.transform = `translate(0px, ${translateY})`;
      }
    }, 0);

    return () => clearTimeout(timer);
  }, [storyboardType, style, breakpoint, matches, margin, widgetStyle]);

  useEffect(() => {
    initialiseEditor();
  }, [])

  const initialiseEditor = () => {
    const initialState = {
      widgets: widgetProps.children,
      entityType: 'LIVE_ELEMENT'
    };

    dispatch(createEditorSession(liveElementEditorSession.id, initialState));
    setWidgetContextState({
      imageCDN: IMAGE_CDN_ENDPOINT,
      shouldZoom: true,
      selector: `editors_live_element_${id}.present`,
      storyboard
    })
  }

  return (
    <div id={id}
      ref={ref}
      className={cx(className, styles.liveElement, 'cs_live_element', {
        [styles.mobile]: breakpoint === 'mobile'
      })}
      data-widget-id={id}
      data-widget-type={type}
      data-container={false}
      data-selectable={true}
      style={widgetStyle}
    >
      <div className={styles.overlay}></div>
      
      <BrandThemeManager className={`#${id} .cs_text_widget`} themes={widgetProps.themes} />
      {widgets.length > 0 &&
      <WidgetsContext.Provider value={widgetContextState}>
        <FormContext.Provider value={{ error: {} }}>
          {widgets.filter(item => item.type === LIVE_ELEMENT_CONTAINER).map((widget, index) => {
            return <Widgets key={widget.uid} widgetProps={widget} breakpoint={breakpoint} />
          })}
        </FormContext.Provider>
      </WidgetsContext.Provider>
      } 
      {widgets.length === 0 && 
        <div className={styles.elementDeleted}>
          <i className={cx("cleverstory-broken", styles.icon)} />
          <span className={styles.label}>Element Deleted</span>
        </div>  
      }
    </div >
  )
}

const liveElementsWidgetEquals = function (prevProps, nextProps) {
  const paths = ['className', 'style', 'widgetProps', 'widgetRef'];
  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
}

const LiveElementsWidget = React.memo(LiveElementsWidgetComponent, liveElementsWidgetEquals);

const LiveElementDesktopAction = (props) => {
  const [view, setView] = useState(null);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const id = _.get(props, 'uid');

  const workspace = useSelector((state) => _.get(state, "workspaces.workspace.item"));
  const brandkitEnabled = _.get(workspace, 'brandkitEnabled', false);

  const allowBrandkit = useSelector((state) =>
    _.get(state, `${editorSession}.present.entityType`)) !== 'PALETTE_COMPONENT';
  
  const editable = useSelector((state) => {
    return _.get(state, 'users.me.id') === _.get(props, 'content.createdBy.id');
  });

  return (
    <>
      {props.content && editable &&
        <Property.Action className={styles.iconButton} tooltip='Edit live element' onClick={() => setView('editLiveElement')}>
          <i className={'cleverstory-edit'} />
        </Property.Action>
      }
      
      {props.content && 
        <Property.Action className={styles.iconButton} tooltip='Break live element' onClick={() => setView('breakLiveElement')}>
          <i className={'cleverstory-break-live-element'} />
        </Property.Action>
      }

      {brandkitEnabled && allowBrandkit && userRoleService.canManageUsers() &&
        <Property.Action className={styles.iconButton} tooltip='Add to Brand Kit' onClick={() => setView('brandMedia')}>
          <i className={'cleverstory-brandmedia'} />
        </Property.Action>
      }

      {view === 'breakLiveElement' &&
        <BreakLiveElementDialog
          editorSession={editorSession}
          widget={props}
          onCancel={() => {
            setView(null);
          }}
        />
      }

      {view === 'editLiveElement' &&
        <EditLiveElementDialog
          editorSession={editorSession}
          widget={props}
          onCancel={() => {
            setView(null);
          }}
        />
      }
      {view === 'brandMedia' &&
        <BrandComponentProperty type={'SECTIONS'} widgets={[props]} onCancel={() => setView(null)} />
      }
    </>
  );
}

let liveElementWidget = [];
LiveElementsWidget.moveableConfig = (editorSession, widget) => {
  if (widget !== LIVE_ELEMENT) {
    liveElementWidget = widget;
  }

  const scrollContainer = document.querySelector('.editor_widget_scroll_container');
  const className = scrollContainer?.className || '';
  const breakpoint = className.includes('mobile') ? 'mobile' : 'desktop';

  let matches = false;
  let isStretched = false;

  liveElementWidget.children?.forEach(item => {
    const isMatchingType = item.type === MENU || item.type === SECTION;
    const stretched = item.properties[breakpoint]?.stretched === true;
  
    if (isMatchingType) {
      matches = true;
      if (stretched) {
        isStretched = true;
      }
    }
  });

  return {
    draggable: true,
    resizable: false,
    rotatable: matches ? false : true,
    selectable: true,
    cloneable: true,
    deletable: true,
    renderDirections: [],
    onDrag: matches ? (layout) => {
      const frame = new Frame(layout);

      const element = document.querySelector('[data-storyboard-type]');
      const storyboardType = element?.getAttribute('data-storyboard-type');

      if((storyboardType === 'SPATIAL' || storyboardType === 'INTERACTIVE_CONTENT') || !isStretched || breakpoint === 'mobile') {
        frame.set('width', '100%');
      } else {
        frame.set('width', `calc(var(--editor-width) - 2 * 0px)`)
      }

      const translate = frame.get("transform", "translate");
      const values = _.split(translate, ",");
      var y = parseInt(values[1]);
      if (['INTERACTIVE_CONTENT', 'SPATIAL'].includes(storyboardType)){
        y = Math.max(0, y);
      }
      const newTranslate = `0px, ${y}px`;
      frame.set("transform", "translate", newTranslate);

      return frame.get();
    } : undefined,
    onResize: (widget, layout, breakpoint) => {
      const frame = new Frame(layout);
      frame.set('width', 'auto');
      frame.set('height', 'auto');

      const element = document.querySelector('[data-storyboard-type]');
      const storyboardType = element?.getAttribute('data-storyboard-type')

      if(matches) {
        if((storyboardType === 'SPATIAL' || storyboardType === 'INTERACTIVE_CONTENT') || !isStretched || breakpoint === 'mobile') {
          frame.set('width', '100%');
        } else {
          frame.set('width', `calc(var(--editor-width) - 2 * 0px)`)
        }
      }
      return frame.get();
    },
    onReparent: (widget, parent) => {
      var parentElement = parent ? document.getElementById(parent) : null;
      if (parentElement && parentElement.getAttribute('data-widget-type') === HOTSPOT || parentElement.getAttribute('data-widget-type') === CONTENT_CARD) {
        return false;
      }

      return true;
    }
  }
}

LiveElementsWidget.actions = (editor, breakpoint, props) => {
  return (
    <LiveElementDesktopAction
      editor={editor}
      breakpoint={breakpoint}
      {...props}
    />
  );
}

LiveElementsWidget.processRequest = (widget) => {
  const updated = _.omit(widget, 'children');

  return {
    ...updated,
    content: _.get(widget, 'content.id'),
  };
}

export { LiveElementsWidget }